import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import { api } from "@/lib/api";
import { Save, ArrowLeft, Plus, Trash2, Info, FileText, X } from "lucide-react";
import NumberInput from "@/components/NumberInput";
import CurrencyInput from "@/components/CurrencyInput";
import AirportSelect from "@/components/AirportSelect";
import AirlineSelect from "@/components/AirlineSelect";
import { SearchSelect, HotelSearchList, ROOM_CATEGORIES, MEAL_PLANS } from "@/components/HotelForm";
import CustomerPicker from "@/components/CustomerPicker";
import { Globe, MapPin, Building2 } from "lucide-react";

const Money = ({ label, testid, value, onChange, wide }) => (
  <label className={`text-xs ${wide ? "md:col-span-2" : ""}`}>
    <div className="uppercase tracking-widest text-slate-500 mb-1">{label}</div>
    <CurrencyInput testid={testid} value={value} onChange={onChange} />
  </label>
);

const TYPES = [
  { v: "flight", l: "Flight Ticket" },
  { v: "hotel", l: "Hotel Booking" },
  { v: "domestic_tour", l: "Domestic Tour Package" },
  { v: "international_tour", l: "International Tour Package" },
];

const emptyState = () => ({
  invoice_type: "flight",
  status: "draft",
  currency: "INR",
  customer: { name: "", company_name: "", gstin: "", address: "", email: "", mobile: "", passport: "", pan: "" },
  booking: { booking_id: "", quotation_number: "", invoice_date: new Date().toISOString().slice(0, 10), due_date: "", sales_executive: "", booking_date: "", travel_from: "", travel_to: "", destination: "", start_city: "", adults: 1, children: 0, infants: 0 },
  flight: { airline: "", pnr: "", ticket_number: "", sector: "", dep_airport: "", arr_airport: "", travel_class: "Economy", passenger_name: "", base_fare: 0, taxes: 0, convenience_fee: 0, airline_charges: 0, service_charges: 0 },
  hotel: { hotel_name: "", hotel_address: "", hotel_place_id: "", hotel_image_url: "", country_code: "", city: "", country: "", check_in: "", check_out: "", room_category: "", meal_plan: "", rooms: 1, nights: 1, room_charges: 0, extra_bed: 0, taxes: 0, service_charges: 0 },
  domestic: { destination: "", package_name: "", hotel: "", transfers: "", sightseeing: "", meals: "", package_cost: 0, service_charges: 0 },
  international: { destination: "", package_name: "", hotel: "", flights: "", visa: "", insurance: "", transfers: "", sightseeing: "", package_cost: 0, service_charges: 0 },
  items: [],
  tax: { gst_mode: "none", gst_rate: 0, interstate: false, tcs_enabled: false, tcs_rate: 5 },
  payment: { bank_name: "", account_holder: "", account_number: "", ifsc: "", branch: "", upi_id: "", upi_qr_url: "" },
  notes: "",
  terms: "As per booking terms & conditions. Invoice must be paid on or before the due date. All disputes subject to jurisdiction of company registered office.",
});

// --- Live preview: mirror backend logic ---
function computePreview(f) {
  let base = 0, service = 0;
  if (f.invoice_type === "flight" && f.flight) {
    base = num(f.flight.base_fare) + num(f.flight.taxes) + num(f.flight.convenience_fee) + num(f.flight.airline_charges);
    service = num(f.flight.service_charges);
  } else if (f.invoice_type === "hotel" && f.hotel) {
    base = num(f.hotel.room_charges) + num(f.hotel.extra_bed) + num(f.hotel.taxes);
    service = num(f.hotel.service_charges);
  } else if (f.invoice_type === "domestic_tour" && f.domestic) {
    base = num(f.domestic.package_cost); service = num(f.domestic.service_charges);
  } else if (f.invoice_type === "international_tour" && f.international) {
    base = num(f.international.package_cost); service = num(f.international.service_charges);
  }
  const items_total = (f.items || []).reduce((s, it) => s + num(it.quantity) * num(it.unit_price), 0);
  const subtotal = base + items_total;
  let gst = 0, service_eff = service;
  if (f.tax.gst_mode === "package_5") { gst = subtotal * 0.05; service_eff = 0; }
  else if (f.tax.gst_mode === "service_18") { gst = service * 0.18; }
  const cgst = f.tax.interstate ? 0 : gst / 2;
  const sgst = f.tax.interstate ? 0 : gst - cgst;
  const igst = f.tax.interstate ? gst : 0;
  const tcs = f.tax.tcs_enabled ? subtotal * (num(f.tax.tcs_rate) / 100) : 0;
  const raw = subtotal + service_eff + gst + tcs;
  const grand = Math.round(raw);
  return { subtotal, service_charges: service_eff, gst_amount: gst, cgst, sgst, igst, tcs_amount: tcs, round_off: grand - raw, grand_total: grand };
}
function num(v) { const n = parseFloat(v); return isNaN(n) ? 0 : n; }
const money = (n) => "₹ " + (n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export default function InvoiceBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const isEdit = Boolean(id);
  const [form, setForm] = useState(emptyState());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    api.get(`/invoices/${id}`).then(({ data }) => setForm({ ...emptyState(), ...data })).catch(() => toast.error("Could not load"));
  }, [id]);

  // Quotation prefill flow
  const [quotations, setQuotations] = useState([]);
  const [prefillOpen, setPrefillOpen] = useState(false);
  const [prefillFilter, setPrefillFilter] = useState("");
  useEffect(() => {
    if (isEdit) return;
    api.get("/quotations").then(({ data }) => setQuotations(data || [])).catch(() => { });
  }, [isEdit]);

  const applyQuotation = async (qid) => {
    try {
      const { data } = await api.get(`/invoices/prefill/quotation/${qid}`);
      setForm((prev) => {
        const next = structuredClone(prev);
        next.customer = { ...prev.customer, ...data.customer };
        next.booking = { ...prev.booking, ...data.booking };
        // Suggest package cost + name into the currently selected type
        const cost = data.suggested_package_cost || 0;
        if (prev.invoice_type === "domestic_tour" && next.domestic) {
          next.domestic.package_cost = cost;
          next.domestic.package_name = data.package_name || next.domestic.package_name;
          next.domestic.destination = data.booking.destination || next.domestic.destination;
        } else if (prev.invoice_type === "international_tour" && next.international) {
          next.international.package_cost = cost;
          next.international.package_name = data.package_name || next.international.package_name;
          next.international.destination = data.booking.destination || next.international.destination;
        }
        return next;
      });
      toast.success(`Prefilled from ${data?.booking?.quotation_number || "quotation"}`);
      setPrefillOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not prefill");
    }
  };

  const quotationsFiltered = useMemo(() => {
    const t = prefillFilter.trim().toLowerCase();
    if (!t) return quotations;
    return quotations.filter((q) => {
      const s = [q.quotation_number, q.package_title, q.lead?.name, q.destination?.country].join(" ").toLowerCase();
      return s.includes(t);
    });
  }, [quotations, prefillFilter]);

  const set = (path, value) => setForm((prev) => {
    const next = structuredClone(prev);
    const keys = path.split(".");
    let cur = next;
    for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]] = cur[keys[i]] ?? {};
    cur[keys[keys.length - 1]] = value;
    return next;
  });

  const preview = useMemo(() => computePreview(form), [form]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        invoice_type: form.invoice_type, status: form.status, currency: form.currency,
        customer: form.customer, booking: form.booking,
        flight: form.invoice_type === "flight" ? form.flight : null,
        hotel: form.invoice_type === "hotel" ? form.hotel : null,
        domestic: form.invoice_type === "domestic_tour" ? form.domestic : null,
        international: form.invoice_type === "international_tour" ? form.international : null,
        items: form.items, tax: form.tax, payment: form.payment,
        notes: form.notes, terms: form.terms,
      };
      const res = isEdit ? await api.put(`/invoices/${id}`, payload) : await api.post(`/invoices`, payload);
      toast.success(isEdit ? "Invoice updated" : `Created ${res.data.invoice_number}`);
      nav(`/invoices/${res.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar role="agent" />
      <main className="flex-1 p-8 lg:p-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <button onClick={() => nav(-1)} className="p-1.5 text-slate-500 hover:text-slate-800"><ArrowLeft size={18} /></button>
            <div>
              <div className="overline text-emerald-700">Invoice</div>
              <h1 className="font-editorial text-3xl text-slate-900">{isEdit ? `Edit ${form.invoice_number || ""}` : "New Invoice"}</h1>
            </div>
            <div className="ml-auto flex gap-2">
              {!isEdit && (
                <button data-testid="inv-prefill-btn" onClick={() => setPrefillOpen(true)} className="inline-flex items-center gap-2 border border-border rounded px-3 py-2 text-xs hover:border-emerald-500">
                  <FileText size={13} /> Prefill from quotation
                </button>
              )}
              <button data-testid="inv-save-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-[#16A34A] hover:bg-emerald-700 disabled:opacity-60 text-white px-4 py-2 rounded text-sm font-semibold">
                <Save size={14} /> {saving ? "Saving…" : (isEdit ? "Update" : "Create Invoice")}
              </button>
            </div>
          </div>

          {prefillOpen && (
            <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setPrefillOpen(false)}>
              <div className="bg-white rounded-lg w-full max-w-lg p-5 max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="font-editorial text-xl flex-1">Pick a quotation to prefill</div>
                  <button onClick={() => setPrefillOpen(false)}><X size={16} className="text-slate-500" /></button>
                </div>
                <input data-testid="prefill-search" autoFocus value={prefillFilter} onChange={(e) => setPrefillFilter(e.target.value)} placeholder="Search by number, package, customer…" className="w-full border border-border rounded px-3 py-2 text-sm mb-3" />
                <div className="overflow-y-auto -mx-1 flex-1">
                  {quotationsFiltered.length === 0 && <div className="text-xs text-slate-500 p-4">No matching quotations.</div>}
                  {quotationsFiltered.map((q) => (
                    <button key={q.id} data-testid={`prefill-pick-${q.id}`} onClick={() => applyQuotation(q.id)} className="w-full text-left px-3 py-2.5 rounded hover:bg-slate-50 border border-transparent hover:border-emerald-200">
                      <div className="flex items-baseline justify-between">
                        <div className="font-mono text-xs text-slate-500">{q.quotation_number || q.id.slice(0, 8)}</div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-400">{q.status}</div>
                      </div>
                      <div className="text-sm font-medium text-slate-900 mt-0.5">{q.package_title || "Untitled package"}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{q.lead?.name || "—"} · {q.destination?.country || q.cities?.[0]?.city || "—"} · ₹ {(q.pricing?.grand_total || 0).toLocaleString("en-IN")}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left form */}
            <div className="lg:col-span-2 space-y-5">
              {/* Type selector */}
              <Card title="Invoice type">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {TYPES.map(t => (
                    <button key={t.v} type="button"
                      data-testid={`type-${t.v}`}
                      onClick={() => set("invoice_type", t.v)}
                      className={`text-xs px-3 py-3 rounded border ${form.invoice_type === t.v ? "border-emerald-500 bg-emerald-50 text-emerald-800 font-semibold" : "border-border hover:border-emerald-400"}`}>
                      {t.l}
                    </button>
                  ))}
                </div>
              </Card>

              {/* Customer */}
              <Card title="Customer">
                <div className="mb-4">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">From CRM</div>
                  <CustomerPicker
                    value={form.customer_ref}
                    onPick={(c) => {
                      const nm = c.customer_type === "corporate" && c.company_name
                        ? c.company_name
                        : [c.title, c.first_name, c.middle_name, c.last_name].filter(Boolean).join(" ");
                      setForm((f) => ({
                        ...f,
                        customer_id: c.id,
                        customer_ref: { id: c.id, customer_id: c.customer_id, name: nm, email: c.email, mobile: c.mobile, customer_type: c.customer_type, company_name: c.company_name },
                        customer: {
                          name: nm || f.customer?.name || "",
                          company_name: c.company_name || f.customer?.company_name || "",
                          gstin: c.company_gstin || f.customer?.gstin || "",
                          pan: c.company_pan || c.pan_number || f.customer?.pan || "",
                          passport: c.passport_number || f.customer?.passport || "",
                          email: c.email || f.customer?.email || "",
                          mobile: c.mobile || f.customer?.mobile || "",
                          address: [c.address, c.city, c.state, c.country, c.pincode].filter(Boolean).join(", ") || f.customer?.address || "",
                        },
                      }));
                    }}
                    onClear={() => setForm((f) => ({ ...f, customer_id: "", customer_ref: null }))}
                    testid="inv-customer-picker"
                  />
                </div>
                <Grid>
                  <Field label="Name" testid="cust-name" value={form.customer.name} onChange={(v) => set("customer.name", v)} />
                  <Field label="Company Name" value={form.customer.company_name} onChange={(v) => set("customer.company_name", v)} />
                  <Field label="GSTIN" value={form.customer.gstin} onChange={(v) => set("customer.gstin", v)} />
                  <Field label="PAN" value={form.customer.pan} onChange={(v) => set("customer.pan", v)} />
                  <Field label="Passport" value={form.customer.passport} onChange={(v) => set("customer.passport", v)} />
                  <Field label="Email" value={form.customer.email} onChange={(v) => set("customer.email", v)} />
                  <Field label="Mobile" value={form.customer.mobile} onChange={(v) => set("customer.mobile", v)} />
                  <Field label="Address" wide value={form.customer.address} onChange={(v) => set("customer.address", v)} />
                </Grid>
              </Card>

              {/* Booking */}
              <Card title="Booking details">
                <Grid>
                  <Field label="Booking ID" value={form.booking.booking_id} onChange={(v) => set("booking.booking_id", v)} />
                  <Field label="Quotation #" value={form.booking.quotation_number} onChange={(v) => set("booking.quotation_number", v)} />
                  <Field label="Invoice Date" type="date" value={form.booking.invoice_date} onChange={(v) => set("booking.invoice_date", v)} />
                  <Field label="Due Date" type="date" value={form.booking.due_date} onChange={(v) => set("booking.due_date", v)} />
                  <Field label="Sales Executive" value={form.booking.sales_executive} onChange={(v) => set("booking.sales_executive", v)} />
                  <Field label="Booking Date" type="date" value={form.booking.booking_date} onChange={(v) => set("booking.booking_date", v)} />
                  <Field label="Travel From" type="date" value={form.booking.travel_from} onChange={(v) => set("booking.travel_from", v)} />
                  <Field label="Travel To" type="date" value={form.booking.travel_to} onChange={(v) => set("booking.travel_to", v)} />
                  <Field label="Destination" value={form.booking.destination} onChange={(v) => set("booking.destination", v)} />
                  <Field label="Start City" value={form.booking.start_city} onChange={(v) => set("booking.start_city", v)} />
                  <div>
                    <div className="uppercase tracking-widest text-slate-500 mb-1 text-xs">Adults</div>
                    <NumberInput testid="booking-adults" value={form.booking.adults} onChange={(v) => set("booking.adults", v)} min={1} max={30} placeholder="1" />
                  </div>
                  <div>
                    <div className="uppercase tracking-widest text-slate-500 mb-1 text-xs">Children</div>
                    <NumberInput testid="booking-children" value={form.booking.children} onChange={(v) => set("booking.children", v)} min={0} max={20} placeholder="0" />
                  </div>
                  <div>
                    <div className="uppercase tracking-widest text-slate-500 mb-1 text-xs">Infants</div>
                    <NumberInput testid="booking-infants" value={form.booking.infants} onChange={(v) => set("booking.infants", v)} min={0} max={10} placeholder="0" />
                  </div>
                </Grid>
              </Card>

              {/* Type-specific */}
              {form.invoice_type === "flight" && (
                <Card title="Flight details">
                  <Grid>
                    <AirlineSelect label="Airline" value={form.flight.airline} onChange={(v) => set("flight.airline", v)} testid="inv-flight-airline" />
                    <Field label="PNR" value={form.flight.pnr} onChange={(v) => set("flight.pnr", v)} />
                    <Field label="Ticket #" value={form.flight.ticket_number} onChange={(v) => set("flight.ticket_number", v)} />
                    <AirportSelect label="From Airport" value={form.flight.dep_airport} onChange={(v) => set("flight.dep_airport", v)} testid="inv-flight-from" />
                    <AirportSelect label="To Airport" value={form.flight.arr_airport} onChange={(v) => set("flight.arr_airport", v)} testid="inv-flight-to" />
                    <Field label="Class" value={form.flight.travel_class} onChange={(v) => set("flight.travel_class", v)} />
                    <Field label="Passenger" value={form.flight.passenger_name} onChange={(v) => set("flight.passenger_name", v)} />
                    <Money label="Base Fare" value={form.flight.base_fare} onChange={(v) => set("flight.base_fare", v)} testid="flight-base-fare" />
                    <Money label="Taxes" value={form.flight.taxes} onChange={(v) => set("flight.taxes", v)} testid="flight-taxes" />
                    <Money label="Convenience Fee" value={form.flight.convenience_fee} onChange={(v) => set("flight.convenience_fee", v)} testid="flight-convenience" />
                    <Money label="Airline Charges" value={form.flight.airline_charges} onChange={(v) => set("flight.airline_charges", v)} testid="flight-airline-charges" />
                    <Money label="Service Charges" value={form.flight.service_charges} onChange={(v) => set("flight.service_charges", v)} testid="flight-service" />
                  </Grid>
                </Card>
              )}
              {form.invoice_type === "hotel" && (
                <Card title="Hotel details">
                  <InvoiceHotelPicker
                    hotel={form.hotel}
                    onPatch={(patch) => setForm((f) => ({ ...f, hotel: { ...f.hotel, ...patch } }))}
                  />
                  <Grid>
                    <Field label="Check-in" type="date" value={form.hotel.check_in} onChange={(v) => set("hotel.check_in", v)} />
                    <Field label="Check-out" type="date" value={form.hotel.check_out} onChange={(v) => set("hotel.check_out", v)} />
                    <div>
                      <div className="uppercase tracking-widest text-slate-500 mb-1 text-xs">Rooms</div>
                      <NumberInput testid="hotel-rooms" value={form.hotel.rooms} onChange={(v) => set("hotel.rooms", v)} min={1} max={50} placeholder="1" />
                    </div>
                    <div>
                      <div className="uppercase tracking-widest text-slate-500 mb-1 text-xs">Nights</div>
                      <NumberInput testid="hotel-nights" value={form.hotel.nights} onChange={(v) => set("hotel.nights", v)} min={1} max={365} placeholder="1" />
                    </div>
                    <Money label="Room Charges" value={form.hotel.room_charges} onChange={(v) => set("hotel.room_charges", v)} testid="hotel-room-charges" />
                    <Money label="Extra Bed" value={form.hotel.extra_bed} onChange={(v) => set("hotel.extra_bed", v)} testid="hotel-extra-bed" />
                    <Money label="Taxes" value={form.hotel.taxes} onChange={(v) => set("hotel.taxes", v)} testid="hotel-taxes" />
                    <Money label="Service Charges" value={form.hotel.service_charges} onChange={(v) => set("hotel.service_charges", v)} testid="hotel-service" />
                  </Grid>
                </Card>
              )}
              {form.invoice_type === "domestic_tour" && (
                <Card title="Domestic package">
                  <Grid>
                    <Field label="Destination" value={form.domestic.destination} onChange={(v) => set("domestic.destination", v)} />
                    <Field label="Package Name" value={form.domestic.package_name} onChange={(v) => set("domestic.package_name", v)} />
                    <Field label="Hotel" value={form.domestic.hotel} onChange={(v) => set("domestic.hotel", v)} />
                    <Field label="Transfers" value={form.domestic.transfers} onChange={(v) => set("domestic.transfers", v)} />
                    <Field label="Sightseeing" value={form.domestic.sightseeing} onChange={(v) => set("domestic.sightseeing", v)} />
                    <Field label="Meals" value={form.domestic.meals} onChange={(v) => set("domestic.meals", v)} />
                    <Money label="Package Cost" value={form.domestic.package_cost} onChange={(v) => set("domestic.package_cost", v)} testid="dom-cost" />
                    {form.tax.gst_mode !== "package_5" && (
                      <Money label="Service Charges" value={form.domestic.service_charges} onChange={(v) => set("domestic.service_charges", v)} testid="dom-service" />
                    )}
                  </Grid>
                </Card>
              )}
              {form.invoice_type === "international_tour" && (
                <Card title="International package">
                  <Grid>
                    <Field label="Destination" value={form.international.destination} onChange={(v) => set("international.destination", v)} />
                    <Field label="Package Name" value={form.international.package_name} onChange={(v) => set("international.package_name", v)} />
                    <Field label="Hotel" value={form.international.hotel} onChange={(v) => set("international.hotel", v)} />
                    <Field label="Flights" value={form.international.flights} onChange={(v) => set("international.flights", v)} />
                    <Field label="Visa" value={form.international.visa} onChange={(v) => set("international.visa", v)} />
                    <Field label="Insurance" value={form.international.insurance} onChange={(v) => set("international.insurance", v)} />
                    <Field label="Transfers" value={form.international.transfers} onChange={(v) => set("international.transfers", v)} />
                    <Field label="Sightseeing" value={form.international.sightseeing} onChange={(v) => set("international.sightseeing", v)} />
                    <Money label="Package Cost" value={form.international.package_cost} onChange={(v) => set("international.package_cost", v)} testid="intl-cost" />
                    {form.tax.gst_mode !== "package_5" && (
                      <Money label="Service Charges" value={form.international.service_charges} onChange={(v) => set("international.service_charges", v)} testid="intl-service" />
                    )}
                  </Grid>
                </Card>
              )}

              {/* Tax config */}
              <Card title="Taxes">
                <div className="grid md:grid-cols-3 gap-3">
                  <label className="text-xs">
                    <div className="uppercase tracking-widest text-slate-500 mb-1">GST Mode</div>
                    <select data-testid="gst-mode" value={form.tax.gst_mode} onChange={(e) => set("tax.gst_mode", e.target.value)} className="w-full border border-border rounded px-2 py-2 text-sm bg-white">
                      <option value="none">No GST</option>
                      <option value="service_18">18% on Service Charges</option>
                      {(form.invoice_type === "domestic_tour" || form.invoice_type === "international_tour") && (
                        <option value="package_5">5% on Total Package</option>
                      )}
                    </select>
                  </label>
                  <label className="text-xs flex items-end gap-2">
                    <input type="checkbox" data-testid="gst-interstate" checked={form.tax.interstate} onChange={(e) => set("tax.interstate", e.target.checked)} />
                    <span className="text-slate-700">Inter-state (charge IGST instead of CGST+SGST)</span>
                  </label>
                  {form.invoice_type === "international_tour" && (
                    <label className="text-xs flex items-end gap-2">
                      <input type="checkbox" data-testid="tcs-enabled" checked={form.tax.tcs_enabled} onChange={(e) => set("tax.tcs_enabled", e.target.checked)} />
                      <span className="text-slate-700">Apply TCS</span>
                    </label>
                  )}
                  {form.tax.tcs_enabled && (
                    <label className="text-xs">
                      <div className="uppercase tracking-widest text-slate-500 mb-1">TCS Rate %</div>
                      <div className="flex gap-1">
                        {[5, 20].map((r) => (
                          <button key={r} type="button" onClick={() => set("tax.tcs_rate", r)} className={`px-3 py-2 text-xs rounded border ${form.tax.tcs_rate === r ? "border-emerald-500 bg-emerald-50" : "border-border"}`}>{r}%</button>
                        ))}
                        <NumberInput
                          testid="tcs-rate"
                          value={form.tax.tcs_rate}
                          onChange={(v) => set("tax.tcs_rate", v)}
                          min={0}
                          max={30}
                          compact
                        />
                      </div>
                    </label>
                  )}
                </div>
                {form.tax.gst_mode === "package_5" && (
                  <div className="mt-3 flex items-start gap-2 text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded p-3">
                    <Info size={14} className="mt-0.5 text-amber-700 shrink-0" />
                    Service charges are hidden and won't be added — 5% GST is applied on total package amount.
                  </div>
                )}
              </Card>

              {/* Items */}
              <Card title="Additional line items (optional)">
                <table className="w-full text-sm">
                  <thead className="text-xs text-slate-500 uppercase tracking-widest">
                    <tr><th className="text-left py-2">Description</th><th className="w-20 text-right">Qty</th><th className="w-32 text-right">Unit Price</th><th className="w-32 text-right">Amount</th><th className="w-10"></th></tr>
                  </thead>
                  <tbody>
                    {form.items.map((it, idx) => (
                      <tr key={idx} className="border-t border-border">
                        <td className="py-1.5"><input value={it.description} onChange={(e) => { const items = [...form.items]; items[idx] = { ...it, description: e.target.value }; set("items", items); }} className="w-full border border-border rounded px-2 py-1.5" /></td>
                        <td className="pl-1">
                          <NumberInput
                            testid={`item-qty-${idx}`}
                            value={it.quantity}
                            onChange={(v) => { const items = [...form.items]; items[idx] = { ...it, quantity: v }; set("items", items); }}
                            min={1} max={9999} compact
                          />
                        </td>
                        <td className="pl-1">
                          <CurrencyInput
                            testid={`item-price-${idx}`}
                            value={it.unit_price}
                            onChange={(v) => { const items = [...form.items]; items[idx] = { ...it, unit_price: v }; set("items", items); }}
                          />
                        </td>
                        <td className="text-right pr-2 font-mono text-sm">{money(num(it.quantity) * num(it.unit_price))}</td>
                        <td className="text-right"><button onClick={() => { const items = [...form.items]; items.splice(idx, 1); set("items", items); }} className="p-1 text-slate-400 hover:text-red-600"><Trash2 size={14} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button data-testid="items-add" onClick={() => set("items", [...form.items, { description: "", quantity: 1, unit_price: 0 }])} className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#16A34A] hover:underline"><Plus size={12} /> Add row</button>
              </Card>

              {/* Payment details */}
              <Card title="Payment details (auto-filled from your profile — editable)">
                <Grid>
                  <Field label="Bank Name" value={form.payment.bank_name} onChange={(v) => set("payment.bank_name", v)} />
                  <Field label="Account Holder" value={form.payment.account_holder} onChange={(v) => set("payment.account_holder", v)} />
                  <Field label="Account #" value={form.payment.account_number} onChange={(v) => set("payment.account_number", v)} />
                  <Field label="IFSC" value={form.payment.ifsc} onChange={(v) => set("payment.ifsc", v)} />
                  <Field label="Branch" value={form.payment.branch} onChange={(v) => set("payment.branch", v)} />
                  <Field label="UPI ID" value={form.payment.upi_id} onChange={(v) => set("payment.upi_id", v)} />
                </Grid>
              </Card>

              {/* Notes / terms */}
              <Card title="Notes & Terms">
                <label className="text-xs">
                  <div className="uppercase tracking-widest text-slate-500 mb-1">Terms & Conditions</div>
                  <textarea data-testid="terms" rows={3} value={form.terms} onChange={(e) => set("terms", e.target.value)} className="w-full border border-border rounded px-3 py-2 text-sm" />
                </label>
                <label className="text-xs block mt-3">
                  <div className="uppercase tracking-widest text-slate-500 mb-1">Notes</div>
                  <textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="w-full border border-border rounded px-3 py-2 text-sm" />
                </label>
              </Card>
            </div>

            {/* Right: live totals + status */}
            <div className="space-y-5">
              <Card title="Status">
                <select data-testid="inv-status" value={form.status} onChange={(e) => set("status", e.target.value)} className="w-full border border-border rounded px-2 py-2 text-sm bg-white">
                  {["draft", "pending", "paid", "partially_paid", "cancelled", "refunded"].map((s) => <option key={s} value={s}>{s.replace("_", " ").toUpperCase()}</option>)}
                </select>
              </Card>
              <Card title="Live totals">
                <div className="space-y-1.5 text-sm">
                  <Row k="Subtotal" v={money(preview.subtotal)} />
                  {preview.service_charges > 0 && <Row k="Service Charges" v={money(preview.service_charges)} />}
                  {preview.gst_amount > 0 && (form.tax.interstate ? (
                    <Row k={`IGST @ ${form.tax.gst_mode === "package_5" ? 5 : 18}%`} v={money(preview.igst)} />
                  ) : (
                    <>
                      <Row k={`CGST`} v={money(preview.cgst)} />
                      <Row k={`SGST`} v={money(preview.sgst)} />
                    </>
                  ))}
                  {preview.tcs_amount > 0 && <Row k={`TCS @ ${form.tax.tcs_rate}%`} v={money(preview.tcs_amount)} />}
                  {preview.round_off !== 0 && <Row k="Round Off" v={money(preview.round_off)} />}
                </div>
                <div className="mt-4 bg-[#0F1E5B] text-white rounded p-4">
                  <div className="text-[10.5px] uppercase tracking-widest text-emerald-300">Grand Total</div>
                  <div className="font-editorial text-2xl">{money(preview.grand_total)}</div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white border border-border rounded p-5">
      <div className="overline mb-4">{title}</div>
      {children}
    </div>
  );
}
function Grid({ children }) {
  return <div className="grid md:grid-cols-2 gap-3">{children}</div>;
}
function Field({ label, value, onChange, type = "text", placeholder, testid, wide }) {
  return (
    <label className={`text-xs ${wide ? "md:col-span-2" : ""}`}>
      <div className="uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <input data-testid={testid} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full border border-border rounded px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none" />
    </label>
  );
}
function Row({ k, v }) {
  return (
    <div className="flex items-baseline justify-between text-sm">
      <div className="text-slate-500">{k}</div>
      <div className="font-mono text-slate-800">{v}</div>
    </div>
  );
}

// ---------- Hotel Invoice: cascading Country → City → Google Places ----------
let _invCountriesCache = null;
async function _fetchInvCountries() {
  if (_invCountriesCache) return _invCountriesCache;
  const { data } = await api.get("/catalog/countries");
  _invCountriesCache = (data || []).map((c) => ({ value: c.code, label: c.name }));
  return _invCountriesCache;
}

function InvoiceHotelPicker({ hotel, onPatch }) {
  const {
    country_code = "", country = "", city = "",
    hotel_name = "", hotel_address = "", hotel_image_url = "", hotel_place_id = "",
    room_category = "", meal_plan = "",
  } = hotel;

  const [roomOther, setRoomOther] = React.useState(!!room_category && !ROOM_CATEGORIES.includes(room_category));
  const [mealOther, setMealOther] = React.useState(!!meal_plan && !MEAL_PLANS.some((m) => m.label === meal_plan));

  const setCountry = (opt) => {
    onPatch({
      country_code: opt.value, country: opt.label,
      city: "", hotel_name: "", hotel_address: "", hotel_image_url: "", hotel_place_id: "",
      room_category: "", meal_plan: "",
    });
    setRoomOther(false); setMealOther(false);
  };
  const setCity = (opt) => {
    onPatch({
      city: opt.label,
      hotel_name: "", hotel_address: "", hotel_image_url: "", hotel_place_id: "",
      room_category: "", meal_plan: "",
    });
    setRoomOther(false); setMealOther(false);
  };
  const setHotel = async (h) => {
    try {
      const { data } = await api.get("/hotels/place-details", { params: { place_id: h.place_id } });
      onPatch({
        hotel_place_id: data.place_id,
        hotel_name: data.name || h.name,
        hotel_address: data.address || h.address || "",
        hotel_image_url: data.image || h.image || "",
        city: data.city || city,
        country: data.country || country,
      });
    } catch {
      onPatch({
        hotel_place_id: h.place_id,
        hotel_name: h.name, hotel_address: h.address || "", hotel_image_url: h.image || "",
      });
    }
  };

  const fetchCountries = async (q) => {
    const list = await _fetchInvCountries();
    const ql = q.trim().toLowerCase();
    if (!ql) return list.slice(0, 60);
    const starts = list.filter((c) => c.label.toLowerCase().startsWith(ql));
    const startsSet = new Set(starts.map((s) => s.value));
    const contains = list.filter((c) => !startsSet.has(c.value) && c.label.toLowerCase().includes(ql));
    return [...starts, ...contains].slice(0, 60);
  };
  const fetchCities = async (q) => {
    if (!country_code) return [];
    const { data } = await api.get("/catalog/cities-by-code", { params: { country_code, q, limit: 40 } });
    return (data || []).map((n) => ({ value: n, label: n }));
  };

  return (
    <div className="space-y-4 mb-2">
      {/* Cascading pickers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">1. Country</label>
          <SearchSelect
            testid="inv-hotel-country"
            value={country ? { value: country_code, label: country } : null}
            placeholder="Select country…"
            fetchOptions={fetchCountries}
            icon={<Globe size={14} className="text-emerald-600 shrink-0" />}
            onPick={setCountry}
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">
            2. City {!country_code && <span className="normal-case tracking-normal text-slate-400">(select country first)</span>}
          </label>
          <SearchSelect
            testid="inv-hotel-city"
            value={city ? { value: city, label: city } : null}
            disabled={!country_code}
            placeholder={country_code ? "Select city…" : "—"}
            fetchOptions={fetchCities}
            icon={<MapPin size={14} className="text-emerald-600 shrink-0" />}
            onPick={setCity}
          />
        </div>
      </div>

      <div>
        <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">
          3. Hotel {!city && <span className="normal-case tracking-normal text-slate-400">(select city first)</span>}
        </label>
        <HotelSearchList
          disabled={!city}
          city={city}
          country={country}
          currentName={hotel_name}
          onPick={setHotel}
          testid="inv-hotel-picker"
        />
        {hotel_name && (
          <div className="mt-2 bg-emerald-50/50 border border-emerald-100 rounded px-3 py-2 text-xs text-slate-700 flex items-start gap-3">
            {hotel_image_url && <img src={hotel_image_url} alt="" className="w-16 h-12 rounded object-cover shrink-0" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-slate-900 truncate">{hotel_name}</div>
              {hotel_address && <div className="text-slate-500 truncate">{hotel_address}</div>}
              <div className="text-slate-500">{city}{country ? `, ${country}` : ""}</div>
              {hotel_place_id && <div className="text-emerald-700 text-[10px] uppercase tracking-widest mt-0.5">✓ Google-linked</div>}
            </div>
          </div>
        )}
      </div>

      {/* Room + Meal — dropdowns with custom escape hatch */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">4. Room Category</label>
          {roomOther ? (
            <div className="flex gap-2">
              <input
                data-testid="inv-hotel-room-other"
                value={room_category}
                onChange={(e) => onPatch({ room_category: e.target.value })}
                placeholder="Custom room category"
                className="flex-1 border border-border rounded px-3 py-2 text-sm"
              />
              <button type="button" onClick={() => { setRoomOther(false); onPatch({ room_category: "" }); }}
                className="text-xs text-slate-500 hover:text-slate-800 underline underline-offset-2">back to list</button>
            </div>
          ) : (
            <SearchSelect
              testid="inv-hotel-room"
              value={room_category ? { value: room_category, label: room_category } : null}
              placeholder="Select room category…"
              icon={<Building2 size={14} className="text-emerald-600 shrink-0" />}
              fetchOptions={async (q) => {
                const opts = [...ROOM_CATEGORIES.map((r) => ({ value: r, label: r })), { value: "__other__", label: "Other (custom)…" }];
                const ql = q.trim().toLowerCase();
                return ql ? opts.filter((o) => o.label.toLowerCase().includes(ql)) : opts;
              }}
              onPick={(o) => o.value === "__other__" ? (setRoomOther(true), onPatch({ room_category: "" })) : onPatch({ room_category: o.value })}
            />
          )}
        </div>
        <div>
          <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">5. Meal Plan</label>
          {mealOther ? (
            <div className="flex gap-2">
              <input
                data-testid="inv-hotel-meal-other"
                value={meal_plan}
                onChange={(e) => onPatch({ meal_plan: e.target.value })}
                placeholder="Custom meal plan"
                className="flex-1 border border-border rounded px-3 py-2 text-sm"
              />
              <button type="button" onClick={() => { setMealOther(false); onPatch({ meal_plan: "" }); }}
                className="text-xs text-slate-500 hover:text-slate-800 underline underline-offset-2">back to list</button>
            </div>
          ) : (
            <SearchSelect
              testid="inv-hotel-meal"
              value={meal_plan ? { value: meal_plan, label: meal_plan } : null}
              placeholder="Select meal plan…"
              fetchOptions={async (q) => {
                const opts = [...MEAL_PLANS.map((m) => ({ value: m.label, label: m.label })), { value: "__other__", label: "Other (custom)…" }];
                const ql = q.trim().toLowerCase();
                return ql ? opts.filter((o) => o.label.toLowerCase().includes(ql)) : opts;
              }}
              onPick={(o) => o.value === "__other__" ? (setMealOther(true), onPatch({ meal_plan: "" })) : onPatch({ meal_plan: o.value })}
            />
          )}
        </div>
      </div>
    </div>
  );
}
